import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';
import { STORAGE_KEYS } from '../constant/storageKeys';

export type MigrationVersion = '2' | '3';

interface UseMigrationVersionPreferenceOptions {
  enabled?: boolean;
}

const isMigrationVersion = (
  version: null | string,
): version is MigrationVersion => version === '2' || version === '3';

const deserializeMigrationVersion = (
  version: string,
): MigrationVersion | null => {
  return isMigrationVersion(version) ? version : null;
};

const cleanInvalidStoredMigrationVersion = () => {
  try {
    const storedVersion = window.sessionStorage.getItem(
      STORAGE_KEYS.MIGRATION_VERSION_STORAGE_KEY,
    );

    if (storedVersion !== null && !isMigrationVersion(storedVersion)) {
      window.sessionStorage.removeItem(
        STORAGE_KEYS.MIGRATION_VERSION_STORAGE_KEY,
      );
    }
  } catch {
    // Storage may be unavailable in private browsing or restricted contexts.
  }
};

export const useMigrationVersionPreference = ({
  enabled = true,
}: UseMigrationVersionPreferenceOptions = {}): MigrationVersion | null => {
  const [searchParams] = useSearchParams();
  const queryVersion = searchParams.get(
    STORAGE_KEYS.MIGRATION_VERSION_SEARCH_PARAM,
  );

  const selectedQueryVersion = isMigrationVersion(queryVersion)
    ? queryVersion
    : null;
  const isResetRequest =
    queryVersion === STORAGE_KEYS.MIGRATION_VERSION_RESET_VALUE;
  const [storedVersion, setStoredVersion, removeStoredVersion] =
    useSessionStorage<MigrationVersion | null>(
      STORAGE_KEYS.MIGRATION_VERSION_STORAGE_KEY,
      null,
      {
        deserializer: deserializeMigrationVersion,
        serializer: (version: MigrationVersion | null) => version ?? '',
      },
    );

  useEffect(() => {
    if (!enabled) return;

    if (isResetRequest) {
      try {
        removeStoredVersion();
      } catch {
        // Storage may be unavailable in private browsing or restricted contexts.
      }

      return;
    }

    cleanInvalidStoredMigrationVersion();

    if (selectedQueryVersion) {
      setStoredVersion(selectedQueryVersion);
    }
  }, [
    enabled,
    isResetRequest,
    removeStoredVersion,
    selectedQueryVersion,
    setStoredVersion,
  ]);

  if (!enabled || isResetRequest) return null;

  return selectedQueryVersion ?? storedVersion;
};
